import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConsultaInternas, SalvaInternaEstatica, RemoveInterna, ArquivaInterna, ConsultaInternasGrupos, SalvaInternasGrupos, ConsultaPermissoes, ConsultaLogins } from '../lib/api';
import { GlobalContext } from '../lib/GlobalVar';
import { podeAcessarTela } from '../lib/acessoTela';
import Swal from 'sweetalert2';
import Icon from './Icon';
import { GrupoModal, MenuMover, MAX_GRUPOS, novoIdGrupo } from './InternasGrupos';
import { PERSONALIZADOS } from '../personalizados/registry';

// Abas normais escondem as ARQUIVADAS; a aba "Arquivadas" mostra so elas.
const TABS = [
    { id: 'todas',      label: 'Todas',        match: p => !p.arquivada },
    { id: 'construtor', label: '✦ Construtor', match: p => (p.tipo === 'built' || p.tipo === 'construtor') && !p.arquivada },
    { id: 'iframe',     label: 'Iframe',       match: p => p.tipo === 'iframe' && !p.arquivada },
    { id: 'html',       label: 'HTML',         match: p => p.tipo === 'html' && !p.arquivada },
    { id: 'react',      label: 'Página',       match: p => p.tipo === 'react' && !p.arquivada },
    { id: 'links',      label: 'Links',        match: p => p.tipo === 'link' && !p.arquivada },
    { id: 'arquivadas', label: '🗄 Arquivadas', match: p => !!p.arquivada }
];

// Icone por tipo de pagina. Telas do Construtor mostram grafico (se tem chart) ou tabela
// — antes usavam 'wand' (varinha), que em tamanho pequeno parecia "/." e confundia.
function iconeDe(p) {
    if (p.tipo === 'built' || p.tipo === 'construtor') return (Array.isArray(p.charts) && p.charts.length) ? 'chart' : 'table';
    if (p.tipo === 'iframe') return 'iframe';
    if (p.tipo === 'html') return 'html';
    if (p.tipo === 'react') return 'grid';
    if (p.tipo === 'link') return 'external';
    return 'pages';
}

const FONTES = [
    { tipo: 'construtor', label: 'Construtor de API',     icon: 'chart',    desc: 'Tabela gerada a partir de SQL ou REST.' },
    { tipo: 'react',      label: 'Página interna (rota)', icon: 'grid',     desc: 'Atalho para uma rota interna do front.' },
    { tipo: 'link',       label: 'Link externo',          icon: 'external', desc: 'URL aberta em nova aba.' },
    { tipo: 'html',       label: 'HTML estático',         icon: 'html',     desc: 'Página estática com HTML inline.' }
];

const SETORES_FALLBACK = ['TI', 'Comercial', 'Marketing', 'Financeiro', 'Operacoes', 'Logistica', 'Relacionamento', 'Diretoria', 'APP'];

export default function Internas() {
    const { user } = useContext(GlobalContext);
    const navigate = useNavigate();
    const [tab, setTab] = useState('todas');
    const [busca, setBusca] = useState('');
    const [paginas, setPaginas] = useState([]);
    const [novaModal, setNovaModal] = useState(false);
    const [tipoEdit, setTipoEdit] = useState(null);
    const [editForm, setEditForm] = useState(null);
    const [setoresDisp, setSetoresDisp] = useState(SETORES_FALLBACK);
    const [usuariosAll, setUsuariosAll] = useState([]);
    const podeEditar = user && (user.setor === 'TI' || user.cargo === 'Administrador');  // [iter5]

    // Ordem pessoal dos cards (arrastar p/ reordenar). Salva por usuario no navegador.
    const ordemKey = `centralbabita.internas.ordem.${(user?.email || '').toLowerCase()}`;
    const [ordem, setOrdem] = useState(() => { try { return JSON.parse(localStorage.getItem(ordemKey) || '[]'); } catch { return []; } });
    const [dragSlug, setDragSlug] = useState(null);
    const chaveDe = (p) => p.slug || p.id;

    // Grupos PESSOAIS (cada usuario organiza as paginas do seu jeito; vivem no servidor, por e-mail).
    // O grupo e so uma etiqueta: a pagina, o acesso e a rota nao mudam.
    const [grupos, setGrupos] = useState([]);          // [{ id, nome, icone }] na ordem escolhida
    const [mapa, setMapa] = useState({});              // { slug: idDoGrupo }
    const [recolhidos, setRecolhidos] = useState([]);  // ids dos grupos fechados
    const [grupoModal, setGrupoModal] = useState(null); // null | { id } (editar) | { aposCriar: slug } | {} (novo)
    const [menuSlug, setMenuSlug] = useState(null);    // card com o menu "Mover para" aberto
    const [dragGrupo, setDragGrupo] = useState(null);  // secao sob o cursor durante o arrasto
    const [ativo, setAtivo] = useState(null);          // secao em foco no indice lateral
    const visaoKey = `centralbabita.internas.visao.${(user?.email || '').toLowerCase()}`;
    // Padrao = Lista (a grade de sempre). Quem escolhe "Grupos" fica com a escolha salva neste navegador.
    const [visao, setVisao] = useState(() => { try { return localStorage.getItem(visaoKey) === 'grupos' ? 'grupos' : 'lista'; } catch { return 'lista'; } });
    const filaSalvar = useRef(Promise.resolve());
    const SEM_GRUPO = '__sem';

    useEffect(() => { reload(); carregarMeta(); carregarGrupos(); }, []);

    // Sempre que a lista muda, mantem a ordem: preserva a salva e anexa novas no fim.
    useEffect(() => {
        if (!paginas.length) return;
        const slugs = paginas.map(chaveDe);
        setOrdem(prev => {
            const merged = [...prev.filter(s => slugs.includes(s)), ...slugs.filter(s => !prev.includes(s))];
            return merged;
        });
        // eslint-disable-next-line
    }, [paginas]);

    useEffect(() => { try { localStorage.setItem(ordemKey, JSON.stringify(ordem)); } catch (_) {} }, [ordem, ordemKey]);

    async function carregarMeta() {
        const [perm, logs] = await Promise.all([ConsultaPermissoes({}), ConsultaLogins({})]);
        if (perm && !perm.Error && Array.isArray(perm.setores) && perm.setores.length) setSetoresDisp(perm.setores);
        setUsuariosAll(Array.isArray(logs) ? logs : ((logs && logs.users) || []));
    }

    async function reload() {
        const resp = await ConsultaInternas({ solicitante: user?.email });
        if (resp.Error) return setPaginas([]);
        setPaginas(Array.isArray(resp) ? resp : (resp.telas || []));
    }

    async function carregarGrupos() {
        const r = await ConsultaInternasGrupos({ solicitante: user?.email });
        if (!r || r.Error) return;   // sem grupos salvos (ou falha): a tela segue como a lista de sempre
        setGrupos(Array.isArray(r.grupos) ? r.grupos : []);
        setMapa(r.mapa && typeof r.mapa === 'object' ? r.mapa : {});
        setRecolhidos(Array.isArray(r.recolhidos) ? r.recolhidos : []);
    }

    // Grava o conjunto inteiro do usuario. Cada chamada leva o estado completo, por isso vai em FILA:
    // se duas gravacoes se cruzassem, a mais velha poderia chegar por ultimo e desfazer a nova.
    function persistir(g, m, r) {
        setGrupos(g); setMapa(m); setRecolhidos(r);
        filaSalvar.current = filaSalvar.current.then(async () => {
            const resp = await SalvaInternasGrupos({ solicitante: user?.email, grupos: g, mapa: m, recolhidos: r });
            if (resp && resp.Error) Swal.fire({ icon: 'warning', title: 'Não consegui salvar os grupos', text: resp.Error });
        });
    }

    // Grupo valido da pagina ('' = sem grupo). Pagina apontando para grupo apagado conta como sem grupo.
    const gidDe = (p) => { const g = mapa[chaveDe(p)]; return grupos.some(x => x.id === g) ? g : ''; };

    function salvarGrupo(dados) {
        const ctx = grupoModal || {};
        if (ctx.id) {
            persistir(grupos.map(g => g.id === ctx.id ? { ...g, ...dados } : g), mapa, recolhidos);
        } else {
            const id = novoIdGrupo();
            persistir([...grupos, { id, ...dados }], ctx.aposCriar ? { ...mapa, [ctx.aposCriar]: id } : mapa, recolhidos);
            // "Novo grupo" do cabecalho: quem cria um grupo quer ve-lo, entao abre na visao Grupos.
            // (Criado pelo menu "Mover para" de um card, fica na visao em que a pessoa estava.)
            if (!ctx.aposCriar && visao !== 'grupos') trocarVisao('grupos');
        }
        setGrupoModal(null);
        setMenuSlug(null);
    }

    async function excluirGrupo(g) {
        const n = paginas.filter(p => mapa[chaveDe(p)] === g.id).length;
        const c = await Swal.fire({
            icon: 'question', title: `Excluir o grupo "${g.nome}"?`,
            text: n ? `As ${n} página(s) dele voltam para "Sem grupo". Nenhuma página é apagada.` : 'O grupo está vazio. Nenhuma página é apagada.',
            showCancelButton: true, confirmButtonText: 'Excluir grupo', cancelButtonText: 'Cancelar'
        });
        if (!c.isConfirmed) return;
        const novoMapa = {};
        Object.keys(mapa).forEach(s => { if (mapa[s] !== g.id) novoMapa[s] = mapa[s]; });
        persistir(grupos.filter(x => x.id !== g.id), novoMapa, recolhidos.filter(x => x !== g.id));
    }

    function subirDescer(id, delta) {
        const i = grupos.findIndex(g => g.id === id);
        const j = i + delta;
        if (i < 0 || j < 0 || j >= grupos.length) return;
        const arr = [...grupos];
        [arr[i], arr[j]] = [arr[j], arr[i]];
        persistir(arr, mapa, recolhidos);
    }

    function alternarRecolhido(id) {
        persistir(grupos, mapa, recolhidos.includes(id) ? recolhidos.filter(x => x !== id) : [...recolhidos, id]);
    }

    function recolherTodos(recolher) { persistir(grupos, mapa, recolher ? grupos.map(g => g.id) : []); }

    // Poe a pagina num grupo ('' = tira do grupo). Se o grupo estava fechado, abre para mostrar onde ela caiu.
    function atribuir(slug, gid) {
        const novo = { ...mapa };
        if (gid) novo[slug] = gid; else delete novo[slug];
        persistir(grupos, novo, gid ? recolhidos.filter(x => x !== gid) : recolhidos);
        setMenuSlug(null);
    }

    // Atalho para quem esta comecando: um grupo por tipo de pagina (Construtor, Paginas, Links...).
    function agruparPorTipo() {
        const defs = [
            { nome: 'Construtor',         icone: 'chart',    casa: p => p.tipo === 'built' || p.tipo === 'construtor' },
            { nome: 'Páginas do sistema', icone: 'grid',     casa: p => p.tipo === 'react' },
            { nome: 'Links externos',     icone: 'external', casa: p => p.tipo === 'link' },
            { nome: 'Iframes',            icone: 'iframe',   casa: p => p.tipo === 'iframe' },
            { nome: 'HTML estático',      icone: 'html',     casa: p => p.tipo === 'html' }
        ];
        const novosGrupos = [];
        const novoMapa = {};
        defs.forEach(d => {
            const itens = paginas.filter(p => !p.arquivada && (podeEditar || podeAcessarTela(user, p)) && d.casa(p));
            if (!itens.length) return;
            const id = novoIdGrupo();
            novosGrupos.push({ id, nome: d.nome, icone: d.icone });
            itens.forEach(p => { novoMapa[chaveDe(p)] = id; });
        });
        if (!novosGrupos.length) return;
        persistir(novosGrupos, novoMapa, []);
    }

    function trocarVisao(v) {
        setVisao(v);
        try { localStorage.setItem(visaoKey, v); } catch (_) {}
    }

    // Vai ate o grupo: abre se estiver fechado e rola ate ele.
    function irPara(id) {
        if (recolhidos.includes(id)) persistir(grupos, mapa, recolhidos.filter(x => x !== id));
        setAtivo(id);
        setTimeout(() => {
            const el = document.getElementById(`ig-sec-${id}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 40);
    }

    function escolherFonte(tipo) {
        setNovaModal(false);
        if (tipo === 'construtor') return navigate('/TI/Construtor');
        setTipoEdit(tipo);
        setEditForm({
            tipo, titulo: '', slug: '', descricao: '', mensagem_padrao: '',
            modo_acesso: 'todos', setores: [], usuarios: [], url: '', rota: '', html: '', target: '_blank', novaAba: false
        });
    }

    async function salvarEstatica() {
        if (!editForm.titulo || !editForm.slug) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Título e slug obrigatórios.' });
        if (editForm.tipo === 'link' && !editForm.url) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'URL obrigatória para link externo.' });
        if (editForm.tipo === 'react' && !editForm.rota) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Rota obrigatória.' });
        if (editForm.tipo === 'iframe' && !editForm.url) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'URL obrigatória.' });

        const resp = await SalvaInternaEstatica({ solicitante: user?.email, pagina: editForm });
        if (resp.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: resp.Error });
        Swal.fire({ icon: 'success', title: 'Página salva', text: `/internas/${editForm.slug}`, timer: 1300, showConfirmButton: false });
        setTipoEdit(null);
        setEditForm(null);
        reload();
    }

    async function remover(p) {
        const c = await Swal.fire({ icon: 'question', title: `Remover "${p.titulo}"?`, showCancelButton: true });
        if (!c.isConfirmed) return;
        const origem = p.tipo === 'built' || p.tipo === 'construtor' ? 'built' : 'estatica';
        const resp = await RemoveInterna({ solicitante: user?.email, slug: p.slug, origem });
        if (resp.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: resp.Error });
        reload();
    }

    // Arquivar/desarquivar: some da listagem mas continua valendo (rota/dashboards).
    async function alternarArquivo(p) {
        const arquivar = !p.arquivada;
        const resp = await ArquivaInterna({ solicitante: user?.email, slug: p.slug, arquivar });
        if (resp.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: resp.Error });
        Swal.fire({ icon: 'success', title: arquivar ? 'Arquivada' : 'Desarquivada', timer: 1000, showConfirmButton: false });
        reload();
    }

    function abrir(p) {
        if (p.tipo === 'link' && p.url) return window.open(p.url, p.target || '_blank');
        const destino = (p.tipo === 'react' && p.rota) ? p.rota : `/internas/${p.slug}`;
        if (p.novaAba) return window.open(destino, '_blank'); // opção "abrir em nova aba"
        return navigate(destino);
    }

    function editar(p) {
        if (p.tipo === 'built' || p.tipo === 'construtor') return navigate(`/TI/Construtor?editar=${p.slug}`);
        setTipoEdit(p.tipo);
        setEditForm({ ...p, setores: (p.setores && p.setores.length) ? p.setores : (p.setor ? [p.setor] : []), usuarios: p.usuarios || [] });
    }

    function uploadHtml(e) {
        const f = e.target.files[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = ev => setEditForm(prev => ({ ...prev, html: ev.target.result }));
        reader.readAsText(f);
    }

    // Arrastar para reordenar: move dragSlug para a posicao do alvo. Se o alvo e de outro grupo, a pagina muda para ele.
    function soltarEm(alvoSlug) {
        if (!dragSlug || dragSlug === alvoSlug) { setDragSlug(null); setDragGrupo(null); return; }
        setOrdem(prev => {
            const arr = prev.filter(s => s !== dragSlug);
            const ti = arr.indexOf(alvoSlug);
            arr.splice(ti < 0 ? arr.length : ti, 0, dragSlug);
            return arr;
        });
        const doGrupo = (slug) => (grupos.some(g => g.id === mapa[slug]) ? mapa[slug] : '');
        if (grupos.length && doGrupo(alvoSlug) !== doGrupo(dragSlug)) atribuir(dragSlug, doGrupo(alvoSlug));
        setDragSlug(null);
        setDragGrupo(null);
    }

    // Soltar no cabecalho/corpo de um grupo: a pagina entra nele, no fim da fila ('' = Sem grupo).
    function soltarNoGrupo(gid) {
        if (!dragSlug) return;
        const slug = dragSlug;
        setOrdem(prev => [...prev.filter(s => s !== slug), slug]);
        atribuir(slug, gid);
        setDragSlug(null);
        setDragGrupo(null);
    }

    const counts = TABS.reduce((acc, t) => ({ ...acc, [t.id]: paginas.filter(t.match).length }), {});
    const idxOrdem = (s) => { const i = ordem.indexOf(s); return i < 0 ? 9999 : i; };
    const filtradas = paginas
        .filter(TABS.find(t => t.id === tab).match)
        .filter(p => podeEditar || podeAcessarTela(user, p))
        .filter(p => !busca || (p.titulo || '').toLowerCase().includes(busca.toLowerCase()) || (p.slug || '').toLowerCase().includes(busca.toLowerCase()))
        .sort((a, b) => idxOrdem(chaveDe(a)) - idxOrdem(chaveDe(b)));

    // Visao agrupada: so nas abas normais (a lista de arquivadas segue plana) e so se o usuario nao escolheu "Lista".
    const agrupado = visao === 'grupos' && tab !== 'arquivadas';
    const filtrando = !!busca || tab !== 'todas';
    const secoes = grupos.map(g => ({ ...g, itens: filtradas.filter(p => gidDe(p) === g.id) }));
    const semGrupo = filtradas.filter(p => !gidDe(p));
    // Com busca/filtro, grupo sem resultado some; sem filtro, grupo vazio continua visivel (e e alvo de arrasto).
    const secoesVis = secoes.filter(s => s.itens.length > 0 || !filtrando);
    const mostraSem = semGrupo.length > 0 || (!!dragSlug && grupos.length > 0);
    const comGrupos = agrupado && grupos.length > 0;
    const secoesKey = comGrupos ? [...secoesVis.map(s => s.id), mostraSem ? SEM_GRUPO : ''].join('|') + '#' + recolhidos.join(',') + '#' + (busca ? 1 : 0) : '';

    // Destaca no indice lateral o grupo que esta na faixa de leitura da tela.
    useEffect(() => {
        if (!comGrupos || typeof IntersectionObserver === 'undefined') return undefined;
        const els = Array.from(document.querySelectorAll('.ig-sec'));
        if (!els.length) return undefined;
        const obs = new IntersectionObserver(entradas => {
            const vis = entradas.filter(e => e.isIntersecting);
            if (vis.length) setAtivo(vis[0].target.getAttribute('data-gid'));
        }, { rootMargin: '-15% 0px -70% 0px' });
        els.forEach(el => obs.observe(el));
        return () => obs.disconnect();
    }, [comGrupos, secoesKey]);

    // Card de uma pagina (o mesmo na grade plana e dentro dos grupos).
    const renderCard = (p) => {
        const chave = chaveDe(p);
        const gAtual = grupos.find(g => g.id === gidDe(p));
        return (
        <div
            className={`page-card${dragSlug === chave ? ' dragging' : ''}${p.arquivada ? ' arquivada' : ''}`}
            key={chave}
            draggable={!p.arquivada}
            onDragStart={() => !p.arquivada && setDragSlug(chave)}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.stopPropagation(); soltarEm(chave); }}
            onDragEnd={() => { setDragSlug(null); setDragGrupo(null); }}
            title={p.arquivada ? 'Arquivada' : 'Arraste para reordenar ou soltar num grupo'}
        >
            <div className="page-card-icon"><Icon name={iconeDe(p)} size={20}/></div>
            <span className="page-card-badge">{(p.tipo || '').toUpperCase()}</span>
            <div className="page-card-title">{p.titulo}</div>
            <div className="page-card-sub">{p.descricao || '—'}</div>
            {grupos.length > 0 && !p.arquivada && (
                <div className="ig-card-grupo">
                    <button
                        className={`ig-pill${gAtual ? '' : ' vazio'}${menuSlug === chave ? ' aberto' : ''}`}
                        onClick={() => setMenuSlug(menuSlug === chave ? null : chave)}
                        title="Mover para um grupo"
                        aria-haspopup="menu"
                        aria-expanded={menuSlug === chave}
                    >
                        <Icon name={gAtual ? gAtual.icone : 'folder'} size={11}/>
                        <span>{gAtual ? gAtual.nome : 'Sem grupo'}</span>
                        <Icon name="chev-down" size={10}/>
                    </button>
                    {menuSlug === chave && (
                        <MenuMover
                            grupos={grupos}
                            atual={gAtual ? gAtual.id : ''}
                            onEscolher={gid => atribuir(chave, gid)}
                            onNovo={() => { setMenuSlug(null); setGrupoModal({ aposCriar: chave }); }}
                            onFechar={() => setMenuSlug(null)}
                        />
                    )}
                </div>
            )}
            <div className="page-card-foot">
                <span className="mono page-card-path">/internas/{p.slug}</span>
                {podeEditar && (<>
                    <button className="btn ghost sm" onClick={() => alternarArquivo(p)} title={p.arquivada ? 'Desarquivar' : 'Arquivar'}>
                        {p.arquivada ? 'Desarquivar' : <Icon name="folder" size={11}/>}
                    </button>
                    {!p.arquivada && <button className="btn ghost sm" onClick={() => editar(p)}>Editar</button>}
                    {!p.arquivada && <button className="btn ghost sm" onClick={() => remover(p)}><Icon name="trash" size={11}/></button>}
                </>)}
                <button className="btn sm" onClick={() => abrir(p)}>
                    Abrir <Icon name="arrow-right" size={10}/>
                </button>
            </div>
        </div>
        );
    };

    // Alvo de arrasto de uma secao (grupo ou "Sem grupo").
    const alvoArrasto = (gid) => ({
        onDragOver: e => { if (dragSlug) { e.preventDefault(); if (dragGrupo !== gid) setDragGrupo(gid); } },
        onDragLeave: e => { if (!e.currentTarget.contains(e.relatedTarget) && dragGrupo === gid) setDragGrupo(null); },
        onDrop: e => { e.preventDefault(); soltarNoGrupo(gid === SEM_GRUPO ? '' : gid); }
    });

    return (
        <div className="page">
            <div className="page-head">
                <div>
                    <div className="eyebrow">
                        <span className="num">03</span> {paginas.length} {paginas.length === 1 ? 'página cadastrada' : 'páginas cadastradas'}
                    </div>
                    <h1 className="page-title">Páginas Internas</h1>
                    <p className="page-sub">Telas dinâmicas: iframe externo, HTML estático, componente React próprio, link externo ou tabela gerada pelo Construtor — todas com a mesma identidade Babita. <span className="muted">Organize em grupos (só você vê os seus) · arraste para reordenar · arquive para tirar da lista (continua valendo em dashboards e pela rota).</span></p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button className="btn ghost" disabled={grupos.length >= MAX_GRUPOS} onClick={() => setGrupoModal({})} title="Criar um grupo para organizar as páginas">
                        <Icon name="folder" size={12}/> Novo grupo
                    </button>
                    {podeEditar && <button className="btn" onClick={() => setNovaModal(true)}>
                        <Icon name="plus" size={12}/> Nova página
                    </button>}
                </div>
            </div>

            <div className="tabs ig-tabs" style={{ justifyContent: 'space-between' }}>
                <div className="ig-tabs-lista" style={{ display: 'flex' }}>
                    {TABS.map(t => (
                        <button key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
                            {t.label.toUpperCase()} <span className="tab-count">{String(counts[t.id]).padStart(2, '0')}</span>
                        </button>
                    ))}
                </div>
                <div className="ig-bar-dir">
                    {tab !== 'arquivadas' && (
                        <div className="ig-seg" role="group" aria-label="Forma de exibir as páginas">
                            <button className={visao === 'grupos' ? 'on' : ''} onClick={() => trocarVisao('grupos')} aria-pressed={visao === 'grupos'}><Icon name="folder" size={12}/> Grupos</button>
                            <button className={visao === 'lista' ? 'on' : ''} onClick={() => trocarVisao('lista')} aria-pressed={visao === 'lista'}><Icon name="grid" size={12}/> Lista</button>
                        </div>
                    )}
                    <input className="input" style={{ width: 220 }} placeholder="Buscar página…" value={busca} onChange={e => setBusca(e.target.value)}/>
                </div>
            </div>

            {agrupado && grupos.length === 0 && (
                <div className="ig-intro">
                    <div className="ig-intro-icone"><Icon name="folder" size={22}/></div>
                    <div className="ig-intro-txt">
                        <div className="ig-intro-titulo">Organize suas páginas em grupos</div>
                        <p>Crie grupos como "Financeiro" ou "Lançamentos" e arraste as páginas para dentro (ou use "Mover para" no card). A organização é só sua — cada pessoa monta a dela, e nada muda para os outros.</p>
                    </div>
                    <div className="ig-intro-acoes">
                        <button className="btn sm" onClick={() => setGrupoModal({})}>Criar primeiro grupo</button>
                        <button className="btn ghost sm" onClick={agruparPorTipo} title="Cria um grupo para cada tipo de página e já distribui">Agrupar por tipo</button>
                    </div>
                </div>
            )}

            {filtradas.length === 0 && <div className="empty">Nenhuma página nesta aba.</div>}

            {!comGrupos ? (
                <div className="pages-grid">
                    {filtradas.map(renderCard)}
                </div>
            ) : (
                <div className="ig-layout">
                    <nav className="ig-rail" aria-label="Grupos de páginas">
                        <div className="ig-rail-titulo"><span>Grupos</span><span>{String(filtradas.length).padStart(2, '0')} páginas</span></div>
                        {secoesVis.map(s => (
                            <button key={s.id} className={`ig-rail-item${ativo === s.id ? ' on' : ''}`} onClick={() => irPara(s.id)}>
                                <Icon name={s.icone} size={14}/><span className="nome">{s.nome}</span><span className="n">{String(s.itens.length).padStart(2, '0')}</span>
                            </button>
                        ))}
                        {mostraSem && (
                            <button className={`ig-rail-item${ativo === SEM_GRUPO ? ' on' : ''}`} onClick={() => irPara(SEM_GRUPO)}>
                                <Icon name="pages" size={14}/><span className="nome">Sem grupo</span><span className="n">{String(semGrupo.length).padStart(2, '0')}</span>
                            </button>
                        )}
                        <div className="ig-rail-rodape">
                            <button className="ig-link" onClick={() => recolherTodos(true)}>Recolher todos</button>
                            <button className="ig-link" onClick={() => recolherTodos(false)}>Expandir todos</button>
                        </div>
                    </nav>

                    <div className="ig-secoes">
                        {secoesVis.map((s, i) => {
                            const aberto = !!busca || !recolhidos.includes(s.id);
                            return (
                                <section key={s.id} id={`ig-sec-${s.id}`} data-gid={s.id} className={`ig-sec${dragGrupo === s.id ? ' drop' : ''}`} {...alvoArrasto(s.id)}>
                                    <header className="ig-sec-head">
                                        <button className="ig-toggle" onClick={() => alternarRecolhido(s.id)} aria-expanded={aberto} title={aberto ? 'Recolher grupo' : 'Expandir grupo'}>
                                            <span className={`ig-chev${aberto ? ' aberto' : ''}`}><Icon name="chev" size={12}/></span>
                                            <span className="ig-sec-icone"><Icon name={s.icone} size={16}/></span>
                                            <h2 className="ig-sec-nome">{s.nome}</h2>
                                            <span className="ig-sec-n">{String(s.itens.length).padStart(2, '0')}</span>
                                        </button>
                                        <div className="ig-sec-acoes">
                                            <button className="icon-btn" title="Subir grupo" disabled={i === 0 || filtrando} onClick={() => subirDescer(s.id, -1)}><Icon name="chev" size={13} style={{ transform: 'rotate(-90deg)' }}/></button>
                                            <button className="icon-btn" title="Descer grupo" disabled={i === secoesVis.length - 1 || filtrando} onClick={() => subirDescer(s.id, 1)}><Icon name="chev" size={13} style={{ transform: 'rotate(90deg)' }}/></button>
                                            <button className="icon-btn" title="Renomear / trocar ícone" onClick={() => setGrupoModal({ id: s.id })}><Icon name="edit" size={13}/></button>
                                            <button className="icon-btn" title="Excluir grupo (as páginas não são apagadas)" onClick={() => excluirGrupo(s)}><Icon name="trash" size={13}/></button>
                                        </div>
                                    </header>
                                    {aberto && (s.itens.length > 0
                                        ? <div className="pages-grid">{s.itens.map(renderCard)}</div>
                                        : <div className="ig-vazio">Grupo vazio — arraste uma página para cá ou use "Mover para" no card.</div>
                                    )}
                                </section>
                            );
                        })}

                        {mostraSem && (
                            <section id={`ig-sec-${SEM_GRUPO}`} data-gid={SEM_GRUPO} className={`ig-sec${dragGrupo === SEM_GRUPO ? ' drop' : ''}`} {...alvoArrasto(SEM_GRUPO)}>
                                <header className="ig-sec-head">
                                    <div className="ig-toggle estatico">
                                        <span className="ig-sec-icone"><Icon name="pages" size={16}/></span>
                                        <h2 className="ig-sec-nome">Sem grupo</h2>
                                        <span className="ig-sec-n">{String(semGrupo.length).padStart(2, '0')}</span>
                                    </div>
                                </header>
                                {semGrupo.length > 0
                                    ? <div className="pages-grid">{semGrupo.map(renderCard)}</div>
                                    : <div className="ig-vazio">Solte aqui para tirar a página do grupo.</div>
                                }
                            </section>
                        )}
                    </div>
                </div>
            )}

            {novaModal && (
                <div className="modal-mask" onClick={() => setNovaModal(false)}>
                    <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
                        <div className="modal-head">
                            <div>
                                <div className="eyebrow"><span className="num">+</span> Nova página interna</div>
                                <div style={{ marginTop: 6, fontSize: 13, color: 'var(--ink-mute)' }}>Escolha a fonte.</div>
                            </div>
                            <button className="icon-btn" onClick={() => setNovaModal(false)}><Icon name="x" size={14}/></button>
                        </div>
                        <div className="modal-body">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, background: 'var(--line)', border: '1px solid var(--line)' }}>
                                {FONTES.map(f => (
                                    <button key={f.tipo} onClick={() => escolherFonte(f.tipo)} style={{ background: 'var(--paper)', border: 'none', padding: '18px 20px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <Icon name={f.icon} size={20}/>
                                            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 300, fontSize: 16 }}>{f.label}</span>
                                        </div>
                                        <div style={{ marginTop: 8, color: 'var(--ink-mute)', fontSize: 12 }}>{f.desc}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {tipoEdit && editForm && (
                <div className="modal-mask" onClick={() => setTipoEdit(null)}>
                    <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
                        <div className="modal-head">
                            <div className="eyebrow">{(FONTES.find(f => f.tipo === tipoEdit) || {}).label || tipoEdit}</div>
                            <button className="icon-btn" onClick={() => setTipoEdit(null)}><Icon name="x" size={14}/></button>
                        </div>
                        <div className="modal-body">
                            <div className="field"><label>Título</label><input className="input" value={editForm.titulo} onChange={e => setEditForm({ ...editForm, titulo: e.target.value })}/></div>
                            <div className="field"><label>Slug</label><input className="input mono" value={editForm.slug} onChange={e => setEditForm({ ...editForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-') })}/></div>
                            <div className="field"><label>Descrição</label><input className="input" value={editForm.descricao} onChange={e => setEditForm({ ...editForm, descricao: e.target.value })}/></div>
                            <div className="field">
                                <label>Mensagem padrão para compartilhar <span className="muted" style={{ fontSize: 11 }}>(opcional)</span></label>
                                <textarea className="textarea" rows={2} value={editForm.mensagem_padrao || ''} onChange={e => setEditForm({ ...editForm, mensagem_padrao: e.target.value })} placeholder="Texto usado como template ao clicar em Compartilhar nesta tela."/>
                            </div>
                            <div className="field">
                                <label>Modo de acesso</label>
                                <select className="select" value={editForm.modo_acesso} onChange={e => setEditForm({ ...editForm, modo_acesso: e.target.value })}>
                                    <option value="todos">Acesso a todos</option>
                                    <option value="setor">Restrito (setor / usuário)</option>
                                </select>
                            </div>
                            {editForm.modo_acesso === 'setor' && (
                                <>
                                <div className="field">
                                    <label>Setores liberados ({(editForm.setores || []).length})</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                        {setoresDisp.map(s => {
                                            const on = (editForm.setores || []).includes(s);
                                            return (
                                                <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--line)', padding: '6px 12px', cursor: 'pointer', background: on ? 'var(--ink)' : 'var(--paper)', color: on ? 'var(--paper)' : 'var(--ink)' }}>
                                                    <input type="checkbox" checked={on} onChange={() => setEditForm(prev => ({ ...prev, setores: (prev.setores || []).includes(s) ? (prev.setores || []).filter(x => x !== s) : [...(prev.setores || []), s] }))} style={{ display: 'none' }} />
                                                    {s}
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="field">
                                    <label>Usuários liberados ({(editForm.usuarios || []).length}) <span className="muted" style={{ fontSize: 11 }}>(além dos setores)</span></label>
                                    <div style={{ maxHeight: 170, overflowY: 'auto', border: '1px solid var(--line)' }}>
                                        {usuariosAll.map(u => {
                                            const on = (editForm.usuarios || []).includes(u.id);
                                            return (
                                                <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid var(--line)', cursor: 'pointer', background: on ? 'rgba(0,0,0,.05)' : 'transparent' }}>
                                                    <input type="checkbox" checked={on} onChange={() => setEditForm(prev => ({ ...prev, usuarios: (prev.usuarios || []).includes(u.id) ? (prev.usuarios || []).filter(x => x !== u.id) : [...(prev.usuarios || []), u.id] }))} />
                                                    <span style={{ flex: 1, fontSize: 13 }}>{u.nome || u.login}</span>
                                                    <span className="muted mono" style={{ fontSize: 11 }}>{u.setor}</span>
                                                </label>
                                            );
                                        })}
                                        {usuariosAll.length === 0 && <div className="muted" style={{ padding: 10, fontSize: 12 }}>Sem usuários carregados.</div>}
                                    </div>
                                    <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Acesso: TI/Admin sempre · quem é de setor liberado · ou usuário marcado. Setores vazio = só os usuários marcados.</div>
                                </div>
                                </>
                            )}

                            {tipoEdit === 'link' && (
                                <>
                                    <div className="field"><label>URL externa</label><input className="input mono" placeholder="https://..." value={editForm.url} onChange={e => setEditForm({ ...editForm, url: e.target.value })}/></div>
                                    <div className="field">
                                        <label>Abrir em</label>
                                        <select className="select" value={editForm.target} onChange={e => setEditForm({ ...editForm, target: e.target.value })}>
                                            <option value="_blank">Nova aba</option>
                                            <option value="_self">Mesma aba</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {tipoEdit === 'react' && (
                                <div className="field">
                                    <label>Rota interna</label>
                                    <select className="select" value={editForm.rota} onChange={e => setEditForm({ ...editForm, rota: e.target.value })}>
                                        <option value="">— escolha —</option>
                                        {Object.entries(PERSONALIZADOS).map(([slug, def]) => <option key={slug} value={`/personalizados/${slug}`}>{def.titulo} (/personalizados/{slug})</option>)}
                                    </select>
                                </div>
                            )}

                            {tipoEdit === 'iframe' && (
                                <div className="field"><label>URL do iframe</label><input className="input mono" placeholder="https://..." value={editForm.url} onChange={e => setEditForm({ ...editForm, url: e.target.value })}/></div>
                            )}

                            {tipoEdit === 'html' && (
                                <>
                                    <div className="field">
                                        <label>Upload de arquivo HTML</label>
                                        <input type="file" accept=".html,.htm,text/html" onChange={uploadHtml}/>
                                    </div>
                                    <div className="field">
                                        <label>Conteúdo HTML (ou cole aqui)</label>
                                        <textarea className="textarea mono" rows={10} value={editForm.html || ''} onChange={e => setEditForm({ ...editForm, html: e.target.value })}/>
                                    </div>
                                </>
                            )}
                            {editForm.tipo !== 'link' && (
                                <div className="field">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={!!editForm.novaAba} onChange={e => setEditForm({ ...editForm, novaAba: e.target.checked })} />
                                        Abrir em nova aba
                                    </label>
                                    <span className="muted" style={{ fontSize: 11 }}>Ao clicar no menu/atalho, abre esta página numa aba nova.</span>
                                </div>
                            )}
                        </div>
                        <div className="modal-foot">
                            <button className="btn ghost sm" onClick={() => setTipoEdit(null)}>Cancelar</button>
                            <button className="btn sm" onClick={salvarEstatica}>Salvar</button>
                        </div>
                    </div>
                </div>
            )}

            {grupoModal && (
                <GrupoModal
                    inicial={grupoModal.id ? grupos.find(g => g.id === grupoModal.id) : null}
                    onSalvar={salvarGrupo}
                    onFechar={() => setGrupoModal(null)}
                />
            )}
        </div>
    );
}
